/**
 * Which registrant an administrator is currently looking at.
 *
 * WHY THIS EXISTS
 *   Admin ESG reporting scopes by putting the registrant in the URL, because
 *   every ESG endpoint takes `<registrant_id>` in its path. The Telemetry Hub
 *   cannot do that: not one of its routes takes a registrant parameter, and all
 *   of them derive scope from the caller's token on the server side.
 *
 *   Threading a registrant through `telemetryApi.js` would mean changing every
 *   call site and every backend route, and would leave two ways of asking the
 *   same question. Instead the selection is held here and the request
 *   interceptor attaches it as a header, so the administrator's hub and the
 *   registrant's hub call byte-for-byte identical endpoints. That is the point:
 *   two surfaces reading the same code cannot drift into telling different
 *   stories about the same account.
 *
 * WHY A MODULE-LEVEL VALUE IS SAFE HERE
 *   It is set only by the administrator wrapper and cleared when that wrapper
 *   unmounts, so it cannot survive into a normal registrant session. And even
 *   if it did, the server ignores the header for anybody who is not an
 *   administrator and on every method except GET and HEAD. The client is a
 *   convenience; the boundary is enforced server-side in
 *   `utils/telemetry_auth.py`.
 */

export const ADMIN_VIEW_HEADER = 'X-Admin-Registrant-Id';

let adminViewRegistrantId = null;
const listeners = new Set();

/**
 * Point every subsequent request at a registrant, or clear the selection.
 *
 * @param {number|string|null} registrantId Registrant to view, or null to stop.
 */
export function setAdminViewRegistrant(registrantId) {
  const next = registrantId === null || registrantId === undefined || registrantId === ''
    ? null
    : String(registrantId);
  if (next === adminViewRegistrantId) return;
  adminViewRegistrantId = next;
  listeners.forEach((fn) => {
    try {
      fn(adminViewRegistrantId);
    } catch (err) {
      console.error('adminView listener failed:', err);
    }
  });
}

/** The registrant currently being viewed, as a string, or null. */
export function getAdminViewRegistrant() {
  return adminViewRegistrantId;
}

/** Subscribe to changes. Returns an unsubscribe function. */
export function onAdminViewChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
